import React, { useState, useEffect } from "react";
import { Box, Typography, Container, Button, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import GitHubIcon from "@mui/icons-material/GitHub";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import InstagramIcon from "@mui/icons-material/Instagram";
import EmailIcon from "@mui/icons-material/Email";
import useSoundFx from "../../hooks/useSoundFx";
import { useThemeContext } from "../../context/ThemeContext";
import Header from "../../layouts/Header/header";
import Footer from "../../layouts/Footer/footer";
import LiquidGlassCard from "../LandingPage/style/LiquidGlassCard";
import projects from "../../data/projects";
import fotoFaza from "../../assets/Image/faza.jpg";
import Reveal from "../../components/Reveal";
import useInView from "../../hooks/useInView";

const greetings = [
  "Hello",
  "Halo",
  "Assalamu'alaikum",
  "Bonjour",
  "Konnichiwa",
  "Namaste",
];

const experiences = [
  {
    company: "PT Bis Data Indonesia (BIGNET)",
    role: "Fulltime Front End Web Developer",
    period: "November 2024 — Present",
    location: "Jakarta, Indonesia",
    description: [
      "I work on VIONA — an enterprise network monitoring platform used by large corporations. It's the most technically demanding project I've been part of, with real-time data streams, 40+ sensor types, and NOC teams depending on the UI around the clock.",
      "One of the results I'm most proud of: a 95% reduction in data loading time on the Provisioning and RTGS Monitoring modules. I got there through profiling, request batching, and rethinking how state was being managed — not guessing.",
      "I also built the NOC Power Tools 'Hacker-Style' Dashboard from scratch — multiple live charts, real-time sensor feeds, all running simultaneously. Getting that to 60fps without the state tree falling apart was a real challenge.",
      "Beyond performance work, I've touched a wide range of modules: Landscape Network Monitoring, Trouble Ticketing, Role Management, Knowledge Base, and several landing page improvements in VIONA 4.",
      "Working closely with backend engineers and QA here has changed how I think about frontend architecture — understanding the data contract deeply makes everything downstream cleaner.",
    ],
    tech: ["React.js", "Next.js", "Vite", "Material-UI", "Redux Toolkit", "SWR", "TypeScript"],
  },
  {
    company: "PT Bejana Investidata Globalindo (BIGIO)",
    role: "Frontend Engineer Intern",
    period: "June 2023 — June 2024",
    location: "Bandung, West Java",
    description: [
      "My internship at BIGIO is where I got my first taste of working on real products with real deadlines. I worked across two client projects — Inovasi360 and Poultry Indonesia — and shipped both.",
      "For Inovasi360, I built a headless WordPress CMS integration with a Next.js frontend so the client's marketing team could manage content without touching code. We hit Go Live on schedule.",
      "For Poultry Indonesia, I led the frontend redesign and added two features readers had been asking for: bilingual support and a site-wide search powered by GraphQL. It was my first time leading a feature end-to-end in a cross-functional team.",
      "That internship year taught me a lot about scope discipline, cross-browser debugging, and what it actually means to ship something — not just build it.",
    ],
    tech: ["React.js", "Next.js", "SASS", "GraphQL", "WordPress"],
  },
];

const PROJECTS_PER_PAGE = 2;

const MainPage = () => {
  const [currentText, setCurrentText] = useState("");
  const [projectPage, setProjectPage] = useState(1);
  const [slideDir, setSlideDir] = useState("none");
  const [animating, setAnimating] = useState(false);
  const navigate = useNavigate();
  const { playClick, playHover } = useSoundFx();
  const { isDark } = useThemeContext();
  const [chipsRef, chipsInView] = useInView();

  const textPrimary = isDark ? "white" : "#0d1b2a";
  const overlayBg = isDark ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.2)";
  const chipBg = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)";
  const chipBorder = isDark ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(0,0,0,0.1)";
  const chipColor = isDark ? "white" : "#0d1b2a";
  const cardBg = isDark ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.65)";
  const cardBorder = isDark ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(0,0,0,0.08)";
  const cardShadow = isDark ? "0 4px 16px 0 rgba(0,0,0,0.3)" : "0 4px 16px 0 rgba(0,0,0,0.08)";
  const expCardBg = isDark ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.75)";
  const expCardBorder = isDark ? "1px solid rgba(255,255,255,0.13)" : "1px solid rgba(0,0,0,0.08)";
  const timelineBg = isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)";

  const changePage = (newPage) => {
    if (animating || newPage === projectPage) return;
    const dir = newPage > projectPage ? "left" : "right";
    setSlideDir(dir);
    setAnimating(true);
    setTimeout(() => {
      setProjectPage(newPage);
      setSlideDir(dir === "left" ? "from-right" : "from-left");
      setTimeout(() => setAnimating(false), 300);
    }, 250);
  };
  const [index, setIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentWord = greetings[index % greetings.length];
    const typingInterval = setTimeout(
      () => {
        if (isDeleting) {
          setCurrentText(currentWord.substring(0, charIndex - 1));
          setCharIndex((prev) => prev - 1);
        } else {
          setCurrentText(currentWord.substring(0, charIndex + 1));
          setCharIndex((prev) => prev + 1);
        }

        if (!isDeleting && charIndex === currentWord.length) {
          setTimeout(() => setIsDeleting(true), 1000);
        }
        if (isDeleting && charIndex === 0) {
          setIsDeleting(false);
          setIndex((prev) => (prev + 1) % greetings.length);
        }
      },
      isDeleting ? 50 : 150
    );

    return () => clearTimeout(typingInterval);
  }, [charIndex, isDeleting, index]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        color: textPrimary,
        fontFamily: "'DM Sans', sans-serif",
        transition: "color 0.3s, background-color 0.3s",
      }}
    >
      {/* Fixed dark overlay */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: overlayBg,
          zIndex: 1,
          pointerEvents: "none",
        }}
      />

      <Header />

      <Box component="main" id="main-content">

      {/* ─── HERO ─── */}
      <Box
        component="section"
        aria-labelledby="hero-heading"
        id="hero"
        sx={{
          position: "relative",
          zIndex: 2,
          minHeight: { xs: "auto", md: "100vh" },
          display: "flex",
          alignItems: "flex-start",
          pt: { xs: "80px", md: "96px" },
          pb: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <LiquidGlassCard>
            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: { xs: 3, md: 5 }, alignItems: { xs: "center", md: "center" } }}>

              {/* ── Text content ── */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  gutterBottom
                  sx={{
                    minHeight: "40px",
                    fontSize: { xs: "1.3rem", md: "1.75rem" },
                  }}
                >
                  {currentText},{" "}
                  <Box component="span" sx={{ fontWeight: 300 }}>
                    My Name is
                  </Box>
                </Typography>
                <Typography
                  component="h1"
                  id="hero-heading"
                  fontWeight={500}
                  gutterBottom
                  sx={{ fontSize: { xs: "1.6rem", md: "2.5rem" }, lineHeight: 1.2 }}
                >
                  Muhammad Faza Hardiansyah.
                </Typography>
                <Typography
                  fontWeight={400}
                  gutterBottom
                  sx={{ fontSize: { xs: "1.2rem", md: "1.75rem" }, opacity: 0.85 }}
                >
                  Front-End Developer · Based in Jakarta
                </Typography>
                <Typography
                  sx={{
                    mt: 2,
                    fontSize: { xs: "1rem", md: "1.35rem" },
                    lineHeight: 1.7,
                  }}
                >
                  I'm a Frontend Developer with 2+ years of experience turning
                  ideas into interfaces. Right now I'm working with{" "}
                  <Box
                    component="a"
                    href="https://www.linkedin.com/company/bignet/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: "#00e676", fontWeight: "bold", textDecoration: "none" }}
                  >
                    BIGNET
                  </Box>{" "}
                  to build products that are fast, solid, and a pleasure to use.
                </Typography>
                <Typography
                  sx={{
                    mt: 2,
                    fontSize: { xs: "1rem", md: "1.35rem" },
                    lineHeight: 1.7,
                  }}
                >
                  Still learning, always building — with a genuine love for
                  creating things that people enjoy using.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, mt: 4, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      playClick();
                      scrollTo("about");
                    }}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: "999px",
                      color: textPrimary,
                      fontWeight: 600,
                      fontSize: "1rem",
                      fontFamily: "'DM Sans', sans-serif",
                      textTransform: "none",
                      borderColor: isDark
                        ? "rgba(255,255,255,0.4)"
                        : "rgba(0,0,0,0.3)",
                      "&:hover": {
                        backgroundColor: isDark
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.05)",
                        borderColor: isDark
                          ? "rgba(255,255,255,0.7)"
                          : "rgba(0,0,0,0.6)",
                      },
                    }}
                  >
                    About Me
                  </Button>
                  <Button
                    onClick={() => {
                      playClick();
                      scrollTo("projects");
                    }}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: "999px",
                      backgroundColor: "#00e676",
                      color: "#0d1b2a",
                      fontWeight: 700,
                      fontSize: "1rem",
                      fontFamily: "'DM Sans', sans-serif",
                      textTransform: "none",
                      "&:hover": { backgroundColor: "#00e676", opacity: 0.85 },
                    }}
                  >
                    View My Work
                  </Button>
                </Box>
              </Box>

              {/* ── Photo frame ── */}
              <Box
                sx={{
                  display: "flex",
                  flexShrink: 0,
                  order: { xs: -1, md: 0 },
                  width: { xs: 150, md: 230 },
                  height: { xs: 190, md: 300 },
                  borderRadius: "20px",
                  overflow: "hidden",
                  position: "relative",
                  background: isDark
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(0,0,0,0.04)",
                  border: isDark
                    ? "1px solid rgba(255,255,255,0.15)"
                    : "1px solid rgba(0,0,0,0.1)",
                  alignItems: "center",
                  justifyContent: "center",
                  // mobile: color directly | desktop: grayscale → color on hover
                  "& img": {
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "top",
                    filter: { xs: "grayscale(0%)", md: "grayscale(100%)" },
                    transition: "filter 0.45s ease",
                  },
                  "&:hover img": {
                    filter: "grayscale(0%)",
                  },
                  // accent bar bottom
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "3px",
                    background: "#00e676",
                    opacity: 0.8,
                  },
                }}
              >
                <Box
                  component="img"
                  src={fotoFaza}
                  alt="Muhammad Faza Hardiansyah"
                />
              </Box>

            </Box>
          </LiquidGlassCard>
        </Container>
      </Box>

      {/* ───ABOUT ─── */}
      <Box
        component="section"
        aria-labelledby="about-heading"
        id="about"
        sx={{
          position: "relative",
          zIndex: 2,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Container maxWidth="lg" sx={{ pt: { xs: 8, md: 6 }, pb: 8 }}>
          <Box sx={{ mb: 6 }}>
            <Box>
              <Reveal>
                <Typography variant="h2" id="about-heading" gutterBottom fontWeight={300}>
                  About me
                </Typography>
              </Reveal>
              <Reveal delay={120}>
                <Typography variant="h5" sx={{ mb: 4 }}>
                  I got into tech out of pure curiosity — and it stuck. I studied
                  IT at{" "}
                  <Box
                    component="a"
                    href="https://www.linkedin.com/school/polban/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: "#f9a825",
                      fontWeight: "bold",
                      textDecoration: "none",
                    }}
                  >
                    Politeknik Negeri Bandung
                  </Box>
                  , discovered a passion for building things on the web, and have
                  been doing it professionally ever since.
                </Typography>
              </Reveal>
              <Reveal delay={220}>
                <Typography variant="h5" sx={{ mb: 4 }}>
                  I'm still learning every day, and honestly that's the part I
                  enjoy most. Every project teaches me something the previous one
                  couldn't.
                </Typography>
              </Reveal>
              <Reveal delay={320}>
                <Typography variant="h5" sx={{ mb: 4 }}>
                  Outside of work: coffee shops, gaming sessions, and whatever's
                  playing on{" "}
                  <Box
                    component="a"
                    href="https://open.spotify.com/user/fazaaaa."
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: "#f9a825",
                      fontWeight: "bold",
                      textDecoration: "none",
                    }}
                  >
                    Spotify
                  </Box>
                  .
                </Typography>
              </Reveal>
              <Reveal delay={400}>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" sx={{ mb: 2 }}>
                    Here are a few technologies I've been working with recently:
                  </Typography>
                  {/* Chip cascade: one observer on parent, each chip staggers via transition-delay */}
                  <Box ref={chipsRef} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {[
                      "React JS",
                      "Next JS",
                      "TypeScript",
                      "JavaScript",
                      "Node.js",
                      "Tailwind CSS",
                      "Material UI",
                      "Git",
                      "Figma",
                      "PostgreSQL",
                    ].map((tech, i) => (
                      <Box
                        key={tech}
                        onMouseEnter={playHover}
                        sx={{
                          backgroundColor: chipBg,
                          color: chipColor,
                          px: 2,
                          py: 0.5,
                          borderRadius: "999px",
                          fontSize: "0.9rem",
                          fontWeight: 500,
                          backdropFilter: "blur(4px)",
                          border: chipBorder,
                          cursor: "default",
                          opacity: chipsInView ? 1 : 0,
                          transform: chipsInView ? "translateY(0) scale(1)" : "translateY(10px) scale(0.95)",
                          transition: `opacity 0.4s ease ${i * 55}ms, transform 0.4s ease ${i * 55}ms`,
                        }}
                      >
                        {tech}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Reveal>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ───PROJECTS ─── */}
      <Box component="section" aria-labelledby="projects-heading" id="projects" sx={{ position: "relative", zIndex: 2 }}>
        <Container maxWidth="lg" sx={{ pt: 8, pb: 8 }}>
          <Reveal>
            <Typography variant="h2" id="projects-heading" fontWeight={300} gutterBottom>
              Projects
            </Typography>
          </Reveal>
          <Reveal delay={120}>
            <Typography variant="h5" sx={{ mb: 6, opacity: 0.7 }}>
              A selection of things I've built.
            </Typography>
          </Reveal>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 3,
              transition: "opacity 0.25s, transform 0.25s",
              opacity: animating ? 0 : 1,
              transform: animating
                ? slideDir === "left"
                  ? "translateX(-30px)"
                  : "translateX(30px)"
                : "translateX(0)",
            }}
          >
            {[
              ...projects.slice(
                (projectPage - 1) * PROJECTS_PER_PAGE,
                projectPage * PROJECTS_PER_PAGE,
              ),
              ...Array(
                Math.max(
                  0,
                  PROJECTS_PER_PAGE -
                    projects.slice(
                      (projectPage - 1) * PROJECTS_PER_PAGE,
                      projectPage * PROJECTS_PER_PAGE,
                    ).length,
                ),
              ).fill(null),
            ].map((project, idx) =>
              project ? (
                <Box
                  key={project.title}
                  sx={{
                    height: "auto",
                    minHeight: "auto",
                    p: { xs: 2.5, md: 4 },
                    borderRadius: { xs: "16px", md: "20px" },
                    background: cardBg,
                    backdropFilter: "blur(25px)",
                    WebkitBackdropFilter: "blur(25px)",
                    border: cardBorder,
                    boxShadow: cardShadow,
                    display: "flex",
                    flexDirection: "column",
                    overflow: { xs: "visible", md: "hidden" },
                    transition: "background 0.3s ease, border-color 0.3s ease",
                  }}
                >
                  {/* Top row: year + View Details */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography
                      variant="overline"
                      sx={{ opacity: 0.5, letterSpacing: 2 }}
                    >
                      {project.year}
                    </Typography>
                    <Box
                      role="button"
                      tabIndex={0}
                      aria-label={`View details for ${project.title}`}
                      onClick={() => {
                        playClick();
                        navigate(`/project/${project.slug}`);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          playClick();
                          navigate(`/project/${project.slug}`);
                        }
                      }}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        cursor: "pointer",
                        color: "#00e676",
                        fontSize: "0.82rem",
                        fontWeight: 600,
                        fontFamily: "'DM Sans', sans-serif",
                        whiteSpace: "nowrap",
                        userSelect: "none",
                        transition: "opacity 0.2s",
                        "&:hover": { opacity: 0.75 },
                      }}
                    >
                      View Details →
                    </Box>
                  </Box>
                  <Typography variant="h5" fontWeight={600} gutterBottom>
                    {project.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      opacity: 0.8,
                      lineHeight: 1.7,
                      mb: 2,
                    }}
                  >
                    {project.description}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {project.tech.map((t) => (
                      <Box
                        key={t}
                        onMouseEnter={playHover}
                        sx={{
                          backgroundColor: chipBg,
                          color: chipColor,
                          px: 1.5,
                          py: 0.4,
                          borderRadius: "999px",
                          fontSize: "0.78rem",
                          fontWeight: 500,
                          backdropFilter: "blur(4px)",
                          border: chipBorder,
                          cursor: "default",
                        }}
                      >
                        {t}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Box
                  key={`ghost-${idx}`}
                  sx={{
                    height: "auto",
                    minHeight: "auto",
                    visibility: "hidden",
                    p: { xs: 2.5, md: 4 },
                    borderRadius: { xs: "16px", md: "20px" },
                    background: cardBg,
                    border: cardBorder,
                  }}
                />
              ),
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1.5,
              mt: 5,
            }}
          >
            <Box
              role="button"
              tabIndex={projectPage > 1 ? 0 : -1}
              aria-label="Previous page"
              aria-disabled={projectPage === 1}
              onClick={() => {
                if (projectPage > 1) {
                  playClick();
                  changePage(projectPage - 1);
                }
              }}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && projectPage > 1) {
                  e.preventDefault();
                  playClick();
                  changePage(projectPage - 1);
                }
              }}
              sx={{
                width: 28,
                height: 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                color:
                  projectPage > 1
                    ? isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)"
                    : isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)",
                cursor: projectPage > 1 ? "pointer" : "default",
                fontSize: "0.85rem",
                transition: "color 0.2s",
                userSelect: "none",
                ...(projectPage > 1 && { "&:hover": { color: "#00e676" } }),
              }}
            >
              ←
            </Box>

            {Array.from(
              { length: Math.ceil(projects.length / PROJECTS_PER_PAGE) },
              (_, i) => i + 1,
            ).map((num) => (
              <Box
                key={num}
                role="button"
                tabIndex={0}
                aria-label={`Go to page ${num}`}
                aria-current={projectPage === num ? "true" : undefined}
                onClick={() => {
                  playClick();
                  changePage(num);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    playClick();
                    changePage(num);
                  }
                }}
                sx={{
                  width: 28,
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  background:
                    projectPage === num
                      ? "rgba(0,230,118,0.15)"
                      : "transparent",
                  color:
                    projectPage === num ? "#00e676" : isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.35)",
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: projectPage === num ? 700 : 400,
                  fontSize: "0.82rem",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  userSelect: "none",
                  "&:hover": { color: "#00e676" },
                }}
              >
                {num}
              </Box>
            ))}

            <Box
              role="button"
              tabIndex={projectPage < Math.ceil(projects.length / PROJECTS_PER_PAGE) ? 0 : -1}
              aria-label="Next page"
              aria-disabled={projectPage >= Math.ceil(projects.length / PROJECTS_PER_PAGE)}
              onClick={() => {
                if (
                  projectPage < Math.ceil(projects.length / PROJECTS_PER_PAGE)
                ) {
                  playClick();
                  changePage(projectPage + 1);
                }
              }}
              onKeyDown={(e) => {
                if (
                  (e.key === "Enter" || e.key === " ") &&
                  projectPage < Math.ceil(projects.length / PROJECTS_PER_PAGE)
                ) {
                  e.preventDefault();
                  playClick();
                  changePage(projectPage + 1);
                }
              }}
              sx={{
                width: 28,
                height: 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                color:
                  projectPage < Math.ceil(projects.length / PROJECTS_PER_PAGE)
                    ? isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)"
                    : isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)",
                cursor:
                  projectPage < Math.ceil(projects.length / PROJECTS_PER_PAGE)
                    ? "pointer"
                    : "default",
                fontSize: "0.85rem",
                transition: "color 0.2s",
                userSelect: "none",
                ...(projectPage <
                  Math.ceil(projects.length / PROJECTS_PER_PAGE) && {
                  "&:hover": { color: "#00e676" },
                }),
              }}
            >
              →
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ───EXPERIENCE ─── */}
      <Box component="section" aria-labelledby="experience-heading" id="experience" sx={{ position: "relative", zIndex: 2 }}>
        <Container maxWidth="lg" sx={{ pt: 8, pb: 12 }}>
          <Reveal>
            <Typography variant="h2" id="experience-heading" fontWeight={300} gutterBottom>
              Experience
            </Typography>
          </Reveal>
          <Reveal delay={120}>
            <Typography variant="h5" sx={{ mb: 6, opacity: 0.7 }}>
              Where I've worked and what I've done.
            </Typography>
          </Reveal>
          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                position: "absolute",
                left: { xs: 16, md: 20 },
                top: 8,
                bottom: 8,
                width: 2,
                backgroundColor: timelineBg,
              }}
            />
            {experiences.map((exp, i) => (
              <Reveal key={i} variant="fadeUp" delay={i * 180}>
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 3, md: 4 },
                  mb: i < experiences.length - 1 ? 5 : 0,
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    width: { xs: 34, md: 42 },
                    flexShrink: 0,
                    display: "flex",
                    justifyContent: "center",
                    pt: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: "#00e676",
                      border: "2px solid rgba(0,230,118,0.3)",
                      boxShadow: "0 0 10px rgba(0,230,118,0.4)",
                      flexShrink: 0,
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    p: 3,
                    borderRadius: "16px",
                    background: expCardBg,
                    backdropFilter: "blur(20px)",
                    WebkitBackdropFilter: "blur(20px)",
                    border: expCardBorder,
                    boxShadow: isDark
                      ? "0 4px 16px rgba(0,0,0,0.2)"
                      : "0 4px 16px rgba(0,0,0,0.06)",
                    transition: "background 0.3s ease, border-color 0.3s ease",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="h5" fontWeight={600}>
                      {exp.role}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        opacity: isDark ? 0.45 : 0.6,
                        fontFamily: "monospace",
                      }}
                    >
                      {exp.period}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{ color: "#00e676", fontWeight: 500, mb: 0.5 }}
                  >
                    {exp.company}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.45, mb: 2 }}>
                    {exp.location}
                  </Typography>
                  <Box component="ul" sx={{ pl: 2.5, mb: 2, mt: 0 }}>
                    {exp.description.map((d, j) => (
                      <Typography
                        component="li"
                        variant="body1"
                        key={j}
                        sx={{ mb: 0.75, opacity: 0.8, lineHeight: 1.7 }}
                      >
                        {d}
                      </Typography>
                    ))}
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {exp.tech.map((t) => (
                      <Box
                        key={t}
                        onMouseEnter={playHover}
                        sx={{
                          backgroundColor: chipBg,
                          color: chipColor,
                          px: 1.5,
                          py: 0.4,
                          borderRadius: "999px",
                          fontSize: "0.78rem",
                          fontWeight: 500,
                          backdropFilter: "blur(4px)",
                          border: chipBorder,
                          cursor: "default",
                        }}
                      >
                        {t}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
              </Reveal>
            ))}
          </Box>
        </Container>
      </Box>

      </Box>{/* /main */}

      <Footer />

      {/* ── Fixed left sidebar: social icons — desktop only ── */}
      <Box
        sx={{
          display: { xs: "none", lg: "flex" },
          flexDirection: "column",
          alignItems: "center",
          position: "fixed",
          left: 32,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 10,
        }}
      >
        {/* line top */}
        <Box sx={{ width: "1px", height: 80, background: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.18)" }} />

        {/* icons */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, py: 1.5 }}>
          {[
            {
              href: "https://www.linkedin.com/in/muhammadfazahardiansyah/",
              label: "LinkedIn profile",
              icon: <LinkedInIcon fontSize="small" />,
              hoverColor: "#0a66c2",
            },
            {
              href: "https://github.com/mfazahardiansyah",
              label: "GitHub profile",
              icon: <GitHubIcon fontSize="small" />,
              hoverColor: isDark ? "white" : "#0d1b2a",
            },
            {
              href: "mailto:fazamhdh95@gmail.com",
              label: "Send email",
              icon: <EmailIcon fontSize="small" />,
              hoverColor: "#00e676",
            },
            {
              href: "https://www.instagram.com/fazamuhammadh/",
              label: "Instagram profile",
              icon: <InstagramIcon fontSize="small" />,
              hoverColor: "#e1306c",
            },
          ].map(({ href, label, icon, hoverColor }) => (
            <IconButton
              key={label}
              component="a"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              onClick={playClick}
              size="small"
              sx={{
                color: isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.4)",
                transition: "color 0.2s, transform 0.2s",
                "&:hover": { color: hoverColor, transform: "translateY(-3px)" },
              }}
            >
              {icon}
            </IconButton>
          ))}
        </Box>

        {/* line bottom */}
        <Box sx={{ width: "1px", height: 80, background: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.18)" }} />
      </Box>

    </Box>
  );
};

export default MainPage;
